import { useEffect, useRef, useState } from 'react'

import { parseISO } from 'date-fns'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  useAccessToken,
  useAccessTokenRoles,
  useDeliveryMethodModal,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { isZeroQuotePlaceholder } from '@b-stock/fe-core/bstock-next/helpers'
import {
  RatesNotFoundResponseError,
  BadRequestResponseError,
} from '@b-stock/fe-core/bstock-next/mutations'
import type {
  OrchestratedListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  getSelectedQuoteQuery,
  myLocationsQuery,
  locationEligibilityQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { useIsPast } from '@b-stock/fe-core/bstock-react'
import type { LocationModel } from '@b-stock/location-api-client'
import { LocationAttribute } from '@b-stock/location-api-client'
import {
  ShipmentType,
  TmsErrorCode,
  TransportMode,
  type CurrencyUOM,
} from '@b-stock/shipment-api-client'

import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'

import { useIsSellerUserOwner } from './useIsSellerUserOwner'

export type AutoQuoteGenerationError =
  | RatesNotFoundResponseError
  | BadRequestResponseError
  | null

export type AutoQuoteGenerationState = {
  isLoading: boolean
  error: AutoQuoteGenerationError
  status: ShippingQuoteStatus | null
  amountCents: number | null
  currency: CurrencyUOM | null
}

/**
 * Signalcraft shipping-quote proposal's terminal status for a quote attempt.
 * `null` means a quote was never applicable in the first place (seller
 * viewing their own listing, logged out, service-account-only, or the
 * auction/offer already ended) — not one of the five real states, and not
 * something the proposal's enum has a slot for.
 */
export type ShippingQuoteStatus =
  | 'no_address'
  | 'quote_pending'
  | 'rate_error'
  | 'no_rate_found'
  | 'rate_available'

// Parcel zero-quote codes whose cause can clear between page loads (a seller
// fills in a missing box dimension, the parcel vendor comes back). Shipment
// re-rates these on the next quote request instead of serving them from cache.
const RETRYABLE_PARCEL_ERROR_CODES: TmsErrorCode[] = [
  TmsErrorCode.NoRatesFound,
  TmsErrorCode.ParcelVendorUnavailable,
]

/**
 * Whether the selected quote is a retryable parcel zero-quote for the buyer's
 * default address, so this page load should ask for a new quote instead of
 * showing the stored one. A zero-quote for any other address is left alone so
 * the retry never moves the buyer to a different address.
 */
const isRetryableParcelZeroQuote = (
  quote: OrchestratedQuote,
  defaultLocation: LocationModel | undefined
): boolean => {
  const errorCode = quote.tmsMetaData?.errorCode
  return (
    isZeroQuotePlaceholder(quote) &&
    quote.transportMode === TransportMode.Parcel &&
    !!errorCode &&
    RETRYABLE_PARCEL_ERROR_CODES.includes(errorCode) &&
    !!defaultLocation &&
    quote.destination.id === defaultLocation.id
  )
}

const useAutoQuoteGeneration = (listing: OrchestratedListingDetails) => {
  const [error, setError] = useState<AutoQuoteGenerationError>(null)
  const isSellerUserOwner = useIsSellerUserOwner(listing)
  const isServiceAccountOnly = useIsServiceAccountOnly()
  const roles = useAccessTokenRoles()
  const isActiveBuyer = roles?.buyer === RoleStatus.Active

  const { _id: listingId } = listing
  const accessToken = useAccessToken()
  const accountId = useDecodedAccessToken()?.accountId

  // Check if auction/offer has ended to skip auto-quote generation
  // Uses same logic as parent component (OfferingActionColumn.tsx:86)
  const offerEndTime = listing.offering?.offerEndTime ?? ''
  const isPastOfferEndTime = useIsPast(parseISO(offerEndTime))
  const auctionOrOfferEnded =
    isPastOfferEndTime || listing.status === 'COMPLETED'

  const userMayNeedQuote =
    !!accessToken &&
    !isSellerUserOwner &&
    !isServiceAccountOnly &&
    isActiveBuyer

  // Fetch locations early (enabled by userMayNeedQuote) to break circular dependency:
  // - selectedQuoteQuery needs defaultLocation in its key
  // - but userNeedsQuote (which would enable myLocations) depends on selectedQuoteQuery.isFetched
  // Solution: fetch myLocations based on userMayNeedQuote instead
  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        accountId: accountId ? [accountId] : [],
      },
    ],
    { enabled: userMayNeedQuote }
  )
  const defaultLocation = myLocations.data?.locations[0]

  // Include destinationLocationId in query key to prevent infinite loop on back navigation.
  // When defaultLocation changes from undefined → locationId, React Query auto-refetches
  // with the new key. We wait for myLocations to resolve before enabling so the key is
  // stable: once isFetched the key is either a real ID (default deliverable exists) or
  // undefined (no default), neither of which changes again this session.
  const selectedQuoteQuery = getSelectedQuoteQuery.useWithOpts(
    [listingId, accountId],
    {
      enabled:
        isActiveBuyer && (!!defaultLocation?.id || myLocations.isFetched),
    }
  )
  const { data: selectedQuote } = selectedQuoteQuery

  // We use !selectedQuote instead of === null because React Query sets data to
  // undefined (not null) when a query errors. On first page load, getSelectedQuoteQuery
  // may receive a 401 (no destinationLocationId yet), leaving selectedQuote as
  // undefined. !selectedQuote catches both null and undefined, ensuring auto-generation
  // fires in either case and seeds the cache with a real quote.
  const userNeedsQuote =
    userMayNeedQuote &&
    selectedQuoteQuery.isFetched &&
    (!selectedQuote ||
      isRetryableParcelZeroQuote(selectedQuote, defaultLocation))

  const hasAttemptedAutoGeneration = useRef(false)

  const { handleNewQuoteSelection } = useDeliveryMethodModal()

  // If these "setup" queries change, update useAuctionActionPostloadQuery
  const eligibility = locationEligibilityQuery.useWithOpts(
    [{ listingId, locationId: [defaultLocation?.id ?? ''] }],
    { enabled: userNeedsQuote && !!defaultLocation }
  )
  const isEligible = eligibility.data?.[0]?.isEligible ?? false

  const shouldAttemptAutoGeneration =
    userNeedsQuote && !!defaultLocation && isEligible && !auctionOrOfferEnded

  // Initiate auto-quote generation
  useEffect(() => {
    const handleError = (error: unknown) => {
      if (
        error instanceof RatesNotFoundResponseError ||
        error instanceof BadRequestResponseError
      ) {
        setError(error)
      } else {
        setError(new BadRequestResponseError(['UNSPECIFIED_ERROR']))
      }
    }

    // FP-1752: Run effect if shouldAttemptAutoGeneration is true AND hasn't run yet.
    // Without the ref check, the effect won't fire if shouldAttemptAutoGeneration
    // is already true on first render (e.g., when data is cached from previous page).
    if (shouldAttemptAutoGeneration && !hasAttemptedAutoGeneration.current) {
      hasAttemptedAutoGeneration.current = true
      handleNewQuoteSelection({
        accessToken,
        // ShipmentType is intended to be identical to
        // CreateQuotesDtoShipmentTypeEnum, but Typescript has trouble recognizing
        // that despite both having identical values.
        shipmentType: (eligibility.data?.[0]?.eligibleShipmentTypes[0] ||
          ShipmentType.Binding) as ShipmentType,
        address: defaultLocation,
        isEligible,
        isAutoGeneration: true,
      })
        .then(() => setError(null))
        .catch((err: unknown) => handleError(err))
    }
    // Note: This effect is intentionally using a reduced dependency array.
    // We want the auto-quote generation to happen only once, once we have
    // the data we need to run it (which will cause `shouldAttemptAutoGeneration`
    // to flip to `true`), and not re-run if listing, defaultLocation, or
    // handleNewQuoteSelection ever end up changing for whatever reason.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [shouldAttemptAutoGeneration])

  // Signalcraft shipping-quote proposal / BP-2830: derive the terminal status
  // here, where every signal it needs already lives, rather than a second
  // hook re-deriving (and risking drifting from) the same gating logic.
  const quoteApplicable = userMayNeedQuote && !auctionOrOfferEnded
  const status: ShippingQuoteStatus | null = !quoteApplicable
    ? null
    : selectedQuote
      ? 'rate_available'
      : error instanceof RatesNotFoundResponseError
        ? 'no_rate_found'
        : error instanceof BadRequestResponseError
          ? 'rate_error'
          : myLocations.isFetched && !defaultLocation
            ? 'no_address'
            : eligibility.isFetched && !isEligible
              ? 'no_rate_found' // location-ineligible folds in, per the proposal
              : 'quote_pending'

  return {
    error,
    // Derived, never effect-raised: the confirm modals toast + close on a
    // single render reporting not-loading with no quote.
    isLoading: status === 'quote_pending',
    status,
    amountCents:
      status === 'rate_available' ? (selectedQuote?.totalPrice ?? null) : null,
    currency:
      status === 'rate_available' ? (selectedQuote?.currency ?? null) : null,
  }
}

export default useAutoQuoteGeneration
